import React from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { DocumentEditButtonProps } from './types';
import { useDocumentEditButton } from './useDocumentEditButton';
import VerticalDotsIcon from '../../../static/assets/svg/more_vert.svg';
import { useDocumentDuplicating } from '../../pages/document-vault/hooks/useDocumentDuplicating';
import MessageModal from '../message-modal/MessageModal';
import {
  addDocumentContract,
  fetchDocumentContracts,
} from '../../slices/document-contracts/documentContractsSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { NWClient } from '../../client/NWClient';

type DocumentDupeData = {
  category?: string;
  employees: number[];
  suppliers: number[];
  customers: number[];
  business_profile_contacts: number[];
  user: number;
  extras?: {
    start_date?: string | null;
    end_date?: string | null;
    subscription?: boolean;
    amount?: number | null;
    currency?: string | null;
    frequency?: string | null;
    is_signed?: boolean;
  };
};

export const DocumentsEditButton = ({
  getDocumentSummaryFn,
  documentVersionsFn,
  deleteDocumentFn,
  duplicateDocumentFn,
  documentLabelsFn,
  createTemplateFn,
  document,
}: DocumentEditButtonProps) => {
  const { t } = useTranslation();
  const { anchorEl, setAnchorEl } = useDocumentEditButton();
  const open = Boolean(anchorEl);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);

  const {
    document: documentToDuplicate,
    setDocument: setDocumentToDuplicate,
    dialogOpen: duplicatingDialogOpen,
    setDialogOpen: setDuplicatingDialogOpen,
    planUpgradingMessage: planUpgradingMessageDuplicating,
    setPlanUpgradingMessage: setPlanUpgradingMessageDuplicating,
    pendingStatus: pendingStatusDuplicating,
    handleDuplicateOriginalDocument,
    handleDuplicateLatestVersion,
  } = useDocumentDuplicating();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleDocumentSummary = () => {
    getDocumentSummaryFn(document);
    handleClose();
  };
  const handleDocumentCreateTemplate = () => {
    createTemplateFn(document);
    handleClose();
  };
  const handleDocumentDelete = () => {
    deleteDocumentFn(document);
    handleClose();
  };
  const handleDocumentVersions = () => {
    documentVersionsFn(document);
    handleClose();
  };
  const handleDocumentLabels = () => {
    documentLabelsFn(document);
    handleClose();
  };
  const handleDocumentDuplicating = () => {
    if ('contract_generation' in document) {
      // Extract the arrays that need to be updated separately
      const relatedArrays: DocumentDupeData = {
        category: document.category || '',
        user: currentUser.id,
        employees: document.employees || [],
        suppliers: document.suppliers || [],
        customers: document.customers || [],
        business_profile_contacts: document.business_profile_contacts || [],
      };
      const categoriesPayload = {
        dc_types: document.dc_types || [],
        dc_workflows: document.dc_workflows || [],
        dc_label: document.dc_label || [],
      };

      // Format extras as an object instead of an array to match backend expectations
      if (document.extras && Array.isArray(document.extras) && document.extras.length > 0) {
        // Take the first item from the array since backend expects a dictionary
        const extra = document.extras[0];
        relatedArrays.extras = {
          start_date: extra.start_date || null,
          end_date: extra.end_date || null,
          subscription: extra.subscription || false,
          amount: Number(extra.amount) || null,
          currency: extra.currency || null,
          frequency: extra.frequency || null,
          is_signed: extra.is_signed || false,
        };
      }

      // Create document copy without these arrays
      const { dc_types, dc_workflows, dc_label, employees, suppliers, customers, extras, ...rest } =
        document;

      const documentCopy = {
        ...rest,
        name: document.name ? `${document.name} - ${t('labels.copy')}` : t('labels.copy'),
      };

      dispatch(addDocumentContract({ data: documentCopy }))
        .then((res: any) => {
          // Update the newly created document with the related arrays
          const newDocumentId = res.payload.id;
          return NWClient.patch(`document-contract/${newDocumentId}/`, null, relatedArrays).then(
            () => {
              return NWClient.patch(
                `tag-document-contract/${newDocumentId}`,
                null,
                categoriesPayload
              );
            }
          );
        })
        .then(() => {
          dispatch(fetchDocumentContracts({ data: { user: currentUser.id } }));
          toast.success(t('messages.documentDuplicated'));
        })
        .catch((error) => {
          if (
            error.response?.data?.errors?.length > 0 &&
            error.response?.data?.errors[0] === t('messages.exceededPlanLimits')
          ) {
            setPlanUpgradingMessageDuplicating(true);
          } else {
            toast.error(error.message ? error.message : t('messages.errorOccurred'));
          }
        });
    } else {
      setDocumentToDuplicate(document);
      setDuplicatingDialogOpen(true);
      handleClose();
    }
  };

  return (
    <>
      <IconButton size='small' onClick={handleClick}>
        <VerticalDotsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {Boolean(getDocumentSummaryFn) && (
          <MenuItem
            onClick={() => {
              handleDocumentSummary();
            }}
          >
            <span> {t('buttons.summary')}</span>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => {
            handleDocumentDuplicating();
          }}
        >
          <span> {t('buttons.copyDocument')}</span>
        </MenuItem>

        {!document?.contract_generation && Boolean(createTemplateFn) && (
          <MenuItem
            onClick={() => {
              handleDocumentCreateTemplate();
            }}
          >
            <span> {t('buttons.createTemplate')}</span>
          </MenuItem>
        )}
        {Boolean(documentVersionsFn) && (
          <MenuItem
            onClick={() => {
              handleDocumentVersions();
            }}
          >
            <span>{t('buttons.versions')}</span>
          </MenuItem>
        )}
        {Boolean(documentLabelsFn) && (
          <MenuItem
            onClick={() => {
              handleDocumentLabels();
            }}
          >
            <span> {t('buttons.labels')}</span>
          </MenuItem>
        )}
        {Boolean(deleteDocumentFn) && (
          <MenuItem
            onClick={() => {
              handleDocumentDelete();
            }}
          >
            <span> {t('buttons.delete')}</span>
          </MenuItem>
        )}
      </Menu>
      {duplicatingDialogOpen && (
        <MessageModal
          open={duplicatingDialogOpen}
          modalMessage={t('messages.duplicatingVersionMessage')}
          actionButtonText={t('buttons.duplicateLatestDocument')}
          actionButtonFn={() => handleDuplicateLatestVersion(documentToDuplicate)}
          actionButtonAdditionalText={t('buttons.duplicateOriginalDocument')}
          actionButtonAdditionalFn={() => handleDuplicateOriginalDocument(documentToDuplicate)}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDocumentToDuplicate(null);
            setDuplicatingDialogOpen(false);
          }}
        />
      )}
    </>
  );
};
